/**
 * <cascalo-quick-add>: progressive-enhancement "Add" for Cascalo forms (cart cross-sell, product cards, FBT).
 *
 * Wraps a regular <form action="/cart/add" method="post"> so it still works without JavaScript
 * (falls back to the cart page). With JavaScript it adds the item(s) via AJAX and dispatches Horizon's
 * standard CartLinesUpdateEvent, so the cart drawer opens/morphs and the cart count updates —
 * the shopper never leaves the page. Supports one item (name="id") or several (name="items[n][id]").
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/* Horizon declares `const Theme` globally (not a window property). */
const routes = () => (typeof Theme !== 'undefined' ? Theme.routes : null); // eslint-disable-line no-undef

class CascaloQuickAdd extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('button[type="submit"]');
    this.status = this.querySelector('[data-quick-add-status]');
    this.form?.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  getItems() {
    const data = new FormData(this.form);
    const single = data.get('id');
    if (single) return [{ id: Number(single), quantity: Number(data.get('quantity') || 1) }];
    const items = [];
    for (let i = 0; data.get(`items[${i}][id]`); i++) {
      items.push({ id: Number(data.get(`items[${i}][id]`)), quantity: Number(data.get(`items[${i}][quantity]`) || 1) });
    }
    return items;
  }

  setBusy(busy, label) {
    if (!this.button) return;
    this.button.disabled = busy;
    this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (label && this.status) this.status.textContent = label;
  }

  onSubmit = async (event) => {
    const items = this.getItems();
    if (!items.length || !routes()?.cart_add_url) return; // native POST fallback
    event.preventDefault();

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    this.setBusy(true, 'Adding to cart…');
    try {
      const response = await fetch(routes().cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Add to cart failed');

      const cart = await (await fetch(`${routes().cart_url}.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'cascalo-quick-add', sections: result.sections, didError: false },
      });
      this.setBusy(false, 'Added to cart.');
    } catch (error) {
      this.dispatchEvent(new CartErrorEvent({ error: String(error.message || error), code: 'INVALID' }));
      deferred.reject(error);
      this.setBusy(false, 'Could not add this item. Please try again.');
    }
  };
}

if (!customElements.get('cascalo-quick-add')) customElements.define('cascalo-quick-add', CascaloQuickAdd);
